import ModalToOpenService from "@core-ui/ContextServices/ModalToOpenService/ModalToOpenService";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import type { FormProps } from "@ext/catalog/actions/propsEditor/logic/createFormSchema";
import ModalErrorHandler from "@ext/errorHandlers/client/components/ModalErrorHandler";
import t from "@ext/localization/locale/translate";
import { PopoverIconPicker } from "@ext/markdown/elements/icon/edit/components/IconPicker/PopoverIconPicker";
import EditCustomTheme from "@ext/workspace/components/EditCustomTheme";
import { useWorkspaceEditorActions } from "@ext/workspace/components/logic/useWorkspaceEditorActions";
import { useWorkspaceAi } from "@ext/workspace/components/useWorkspaceAi";
import type { ClientWorkspaceConfig } from "@ext/workspace/WorkspaceConfig";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@ui-kit/Button";
import { Description } from "@ui-kit/Description";
import { Dialog, DialogBody, DialogContent } from "@ui-kit/Dialog";
import { Divider } from "@ui-kit/Divider";
import { Form, FormField, FormFooter, FormHeader, FormSectionTitle, FormStack } from "@ui-kit/Form";
import { Icon } from "@ui-kit/Icon";
import { Input, TextInput } from "@ui-kit/Input";
import { Loader } from "@ui-kit/Loader";
import { type Dispatch, type SetStateAction, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface WorkspaceSettingsModalProps {
	workspace: ClientWorkspaceConfig;
	onSubmit?: (props: ClientWorkspaceConfig) => void;
	onClose?: () => void;
	onOpenChange?: Dispatch<SetStateAction<boolean>>;
}

const EditWorkspaceForm = (props: WorkspaceSettingsModalProps) => {
	const { workspace, onOpenChange, onSubmit: onSubmitParent, onClose } = props;

	const {
		open,
		setOpen,
		originalProps,
		workspaces,
		pathPlaceholder,
		removeWorkspace,
		onSubmit,
		workspaceLogoProps,
		workspaceStyleProps,
	} = useWorkspaceEditorActions(workspace);

	const {
		saveData: saveAiData,
		checkServer: checkAiServer,
		getData: getAiData,
		checkToken: checkAiToken,
		isChecking: isAiChecking,
		isSaving: isAiSaving,
	} = useWorkspaceAi(workspace.path);

	const { isTauri } = usePlatform();
	const askPath = isTauri;

	const isNameUnique = (name: string) =>
		name.length > 0 && !workspaces.find((w) => w.path !== originalProps.path && w.name === name);

	const isPathValid = (path: string) => path.length > 0;

	const formSchema = z.object({
		name: z
			.string()
			.min(2, { message: t("space-name-min-length") })
			.refine(isNameUnique, { message: t("cant-be-same-name") }),
		icon: z.optional(z.string()),
		logo: z
			.object({
				light: z.null().optional(),
				dark: z.null().optional(),
			})
			.optional(),
		path: z.optional(
			z
				.string()
				.min(2, { message: t("space-name-min-length") })
				.refine(isPathValid, { message: t("cant-be-same-path") }),
		),
		lfs: z.object({ patterns: z.optional(z.array(z.string())) }).optional(),
		ai: z
			.object({
				apiUrl: z.string().optional().or(z.literal("")),
				token: z.string().optional().or(z.literal("")),
			})
			.refine(
				async (val) => {
					if (!val.apiUrl) return true;
					return await checkAiServer(val.apiUrl);
				},
				{ message: t("workspace.ai-server-error"), path: ["apiUrl"] },
			)
			.refine((val) => !(val.apiUrl && !val.token), {
				message: t("workspace.ai-token-set-error"),
				path: ["token"],
			})
			.refine(
				async (val) => {
					if (!val.apiUrl || !val.token) return true;
					return await checkAiToken(val.apiUrl, val.token);
				},
				{ message: t("workspace.ai-token-error"), path: ["token"] },
			)
			.optional(),
	});

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: async () => {
			const ai = await getAiData();
			return {
				...originalProps,
				lfsPatterns: originalProps.git?.lfs ?? [],
				ai: {
					apiUrl: ai?.aiApiUrl,
					token: ai?.aiToken,
				},
			};
		},
		mode: "onChange",
	});

	const onOpenChangeHandler = useCallback(
		(value: boolean) => {
			setOpen(value);
			onOpenChange?.(value);
			if (!value) {
				onClose?.();
				ModalToOpenService.resetValue();
			}
		},
		[onOpenChange, onClose, setOpen],
	);

	const onCloseHandler = useCallback(() => {
		onOpenChangeHandler(false);
	}, [onOpenChangeHandler]);

	const formSubmit = (e) => {
		void form.handleSubmit(async (data) => {
			await onSubmit({ name: data.name, icon: data.icon, path: data.path }, onCloseHandler);
			if (data.ai) await saveAiData({ apiUrl: data.ai.apiUrl, token: data.ai.token });
			onSubmitParent?.(data as ClientWorkspaceConfig);
		})(e);
	};

	const formProps: FormProps = useMemo(() => {
		return {
			labelClassName: "w-44",
		};
	}, []);

	return (
		<Dialog onOpenChange={onOpenChangeHandler} open={open}>
			<DialogContent data-modal-root>
				<ModalErrorHandler onClose={onCloseHandler} onError={() => {}}>
					<Form asChild {...form}>
						<form className="contents" onSubmit={formSubmit}>
							<FormHeader
								description={t("workspace.configure-your-workspace")}
								icon={"settings"}
								title={t("workspace.edit")}
							/>
							<DialogBody>
								<FormStack>
									<FormField
										control={({ field }) => (
											<Input
												{...field}
												autoFocus
												data-qa={t("name")}
												placeholder={t("workspace.enter-name")}
											/>
										)}
										name="name"
										required
										title={t("name")}
										{...formProps}
									/>
									<FormField
										control={({ field }) => (
											<PopoverIconPicker
												disable={["emoji", "file-input", "color"]}
												label={field.value ? field.value : t("icon")}
												onChange={(value) => "code" in value && field.onChange(value.code)}
												onClear={() => field.onChange(undefined)}
												value={field.value ? { code: field.value } : undefined}
											/>
										)}
										name="icon"
										title={t("icon")}
										{...formProps}
									/>
									{askPath && (
										<FormField
											control={({ field }) => (
												<Input
													{...field}
													data-qa={t("working-directory")}
													placeholder={pathPlaceholder}
													readOnly
													title={field.value}
												/>
											)}
											name="path"
											required
											title={t("working-directory")}
											{...formProps}
										/>
									)}
									{originalProps.path && (
										<>
											<Divider />
											<FormSectionTitle>{t("workspace.appearance")}</FormSectionTitle>
											<EditCustomTheme
												{...workspaceStyleProps}
												{...workspaceLogoProps}
												form={form}
												formProps={formProps}
											/>
										</>
									)}
									<Divider />
									<FormSectionTitle>{t("workspace.set-ai-server")}</FormSectionTitle>{" "}
									<Description
										className="text-sm font-normal tracking-tight text-muted"
										style={{ marginTop: "0.25rem" }}
									>
										{t("workspace.set-ai-server-description")}
										<a
											href="https://gram.ax/resources/docs/space/ai-open-source"
											rel="noreferrer"
											target="_blank"
										>
											<Button className="h-auto pt-0 pb-0 px-2" type="button" variant="link">
												{t("more")}
											</Button>
										</a>
									</Description>
									<FormField
										control={({ field }) => (
											<TextInput
												{...field}
												endIcon={isAiChecking && <Loader size="md" style={{ padding: 0 }} />}
												placeholder="https://your-ai-server.com"
											/>
										)}
										description={t("workspace.ai-server-url-description")}
										name="ai.apiUrl"
										title={t("workspace.ai-server-url")}
										{...formProps}
									/>
									<FormField
										control={({ field }) => (
											<TextInput {...field} placeholder="your-server-token" type="password" />
										)}
										description={t("workspace.ai-server-token-description")}
										name="ai.token"
										title={t("workspace.ai-server-token")}
										{...formProps}
									/>
								</FormStack>
							</DialogBody>
							<FormFooter
								leftContent={
									<>
										<Button
											onClick={() => removeWorkspace(onCloseHandler)}
											type="button"
											variant="text"
										>
											{t("delete")}
										</Button>
									</>
								}
								primaryButton={
									<Button disabled={!!isAiChecking || isAiSaving} type="submit" variant="primary">
										{isAiSaving && <Icon icon="loader-circle" />}
										{t("save")}
									</Button>
								}
							/>
						</form>
					</Form>
				</ModalErrorHandler>
			</DialogContent>
		</Dialog>
	);
};

export default EditWorkspaceForm;
